// CC-01 · Editar el texto o adjuntar la carta final de UNA autoridad antes de firmarla
// (`ui/MG-ILE-46.md` y `ui/MG-ILE-1073.md`; AT del Sprint 3 §10; RN-68, RN-69).
//
// Los dos paneles plegables que se abren DENTRO de la tarjeta «Preparar la carta» de cada
// contenedor `carta-de-autoridad`, con la plantilla «formulario» del catálogo (etiqueta arriba,
// primario a la derecha y secundario a su izquierda; en móvil se apilan con el primario abajo).
// La pantalla no decide si se puede editar ni qué efecto tiene sobre la firma: lo dice el
// servidor (`acciones_de_la_carta`, `texto_del_efecto_en_la_firma`). Aquí solo se valida lo que
// la spec pide en el cliente (vacíos, formato y tamaño contra el tope que entrega el servidor);
// el resto lo responde el servidor y se pinta bajo su campo (`campo` del 422) o en el aviso.

import { useEffect, useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { ACEPTA, Button, Field, Modal, SubidaDeArchivo, Textarea } from './ui';

export const TOPE_DEL_TEXTO = 200_000;
export const TOPE_DEL_MOTIVO = 1000;

/** Literales de la spec (`MG-ILE-46.md`, tablas de campos), iguales a los del servidor. */
export const MENSAJES = {
  textoVacio: 'La carta no puede quedar vacía',
  sinMotivo: 'Escribe el motivo: sin motivo no se crea la versión nueva',
  sinArchivo: 'Elige la carta final en PDF',
  noEsPdf: (nombre: string) => `Adjunta la carta final en PDF: «${nombre}» no es un PDF`,
  pesaDemasiado: (nombre: string, bytes: number, tope: number) =>
    `'${nombre}' pesa ${(bytes / 1_048_576).toFixed(1)} MB y el máximo por archivo es ${tope} MB.`,
} as const;

/** El error de un campo del formulario: el del cliente o el que devolvió el servidor (422). */
export type ErroresDeLaVersion = { texto?: string; motivo?: string; archivo?: string };

/** Lo que el cliente comprueba del archivo al elegirlo (extensión y tamaño, decisiones 9 y 11). */
export function errorDelArchivo(archivo: File | null, topeMb: number | undefined): string | undefined {
  if (!archivo) return MENSAJES.sinArchivo;
  const esPdf = /\.pdf$/i.test(archivo.name.trim()) && (!archivo.type || archivo.type === 'application/pdf');
  if (!esPdf) return MENSAJES.noEsPdf(archivo.name);
  if (topeMb && archivo.size > topeMb * 1_048_576) return MENSAJES.pesaDemasiado(archivo.name, archivo.size, topeMb);
  return undefined;
}

/** «182 / 200000». Se anuncia (aria-live) solo al acercarse al tope (90 %), no en cada tecla. */
function Contador({ valor, tope }: Readonly<{ valor: string; tope: number }>) {
  const cerca = valor.length >= tope * 0.9;
  return <span aria-live={cerca ? 'polite' : 'off'}>{valor.length} / {tope}</span>;
}

/** El marco común: región con encabezado enlazado, foco al abrir y Escape = Cancelar. */
function Panel({ titulo, onCancelar, pie, children, 'data-testid': testid }: Readonly<{
  titulo: string; onCancelar: () => void; pie: ReactNode; children: ReactNode; 'data-testid': string;
}>) {
  const idDelTitulo = useId();
  const encabezado = useRef<HTMLHeadingElement | null>(null);
  // Al abrirse, el foco va al encabezado (spec, «Accesibilidad»).
  useEffect(() => { encabezado.current?.focus(); }, []);
  const alTeclear = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); onCancelar(); }
  };
  return (
    <section aria-labelledby={idDelTitulo} onKeyDown={alTeclear} data-testid={testid}
      className="mt-4 rounded-xl border border-hairline bg-surface p-4">
      <h3 id={idDelTitulo} ref={encabezado} tabIndex={-1} className="text-body font-semibold text-ink mb-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-marca rounded-sm">
        {titulo}
      </h3>
      <div className="space-y-3">{children}</div>
      {/* Orden de tabulación = orden visual; en móvil el primario queda abajo. */}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">{pie}</div>
    </section>
  );
}

export function PanelDeEdicion({ version, texto, motivo, errores, ocupado, onTexto, onMotivo, onSalirDe, onGuardar, onCancelar }: Readonly<{
  /** La versión vigente que se abrió (`carta_version`). */
  version: number;
  texto: string; motivo: string; errores: ErroresDeLaVersion; ocupado: boolean;
  onTexto: (v: string) => void; onMotivo: (v: string) => void;
  /** Validación al salir del campo. */
  onSalirDe: (campo: 'texto' | 'motivo') => void;
  onGuardar: () => void; onCancelar: () => void;
}>) {
  const filas = Math.min(24, Math.max(12, texto.split('\n').length + 1));
  const sinMotivo = !motivo.trim();
  return (
    <Panel titulo={`Editar la carta v${version}`} onCancelar={onCancelar} data-testid="carta-panel-edicion"
      pie={(
        <>
          <Button variant="secondary" onClick={onCancelar} disabled={ocupado} data-testid="carta-cancelar-edicion">Cancelar</Button>
          <Button onClick={onGuardar} disabled={ocupado || !texto.trim() || sinMotivo} data-testid="carta-guardar-edicion">
            {ocupado ? 'Guardando…' : `Guardar como v${version + 1}`}
          </Button>
        </>
      )}>
      <Field label="Texto de la carta" required error={errores.texto} contador={<Contador valor={texto} tope={TOPE_DEL_TEXTO} />}>
        <Textarea value={texto} rows={filas} maxLength={TOPE_DEL_TEXTO} spellCheck lang="es"
          onChange={(e) => onTexto(e.target.value)} onBlur={() => onSalirDe('texto')} data-testid="carta-form-texto" />
      </Field>
      <Field label="Motivo del cambio" required error={errores.motivo}
        hint={sinMotivo ? MENSAJES.sinMotivo : undefined} contador={<Contador valor={motivo} tope={TOPE_DEL_MOTIVO} />}>
        <Textarea value={motivo} rows={3} maxLength={TOPE_DEL_MOTIVO} spellCheck lang="es"
          onChange={(e) => onMotivo(e.target.value)} onBlur={() => onSalirDe('motivo')} data-testid="carta-form-motivo-edicion" />
      </Field>
    </Panel>
  );
}

export function PanelDeAdjunto({ version, archivo, motivo, errores, ocupado, topeMb, textoDeLimites, onArchivo, onMotivo, onSalirDe, onGuardar, onCancelar }: Readonly<{
  version: number;
  archivo: File | null; motivo: string; errores: ErroresDeLaVersion; ocupado: boolean;
  /** Del servidor (`adjunto_de_la_carta`): la pantalla no escribe el tope. */
  topeMb?: number; textoDeLimites?: string;
  onArchivo: (a: File | null) => void; onMotivo: (v: string) => void;
  onSalirDe: (campo: 'motivo') => void;
  onGuardar: () => void; onCancelar: () => void;
}>) {
  const sinMotivo = !motivo.trim();
  return (
    <Panel titulo="Adjuntar la carta final" onCancelar={onCancelar} data-testid="carta-panel-adjunto"
      pie={(
        <>
          <Button variant="secondary" onClick={onCancelar} disabled={ocupado} data-testid="carta-cancelar-adjunto">Cancelar</Button>
          <Button onClick={onGuardar} disabled={ocupado || !archivo || sinMotivo} data-testid="carta-guardar-adjunto">
            {ocupado ? 'Adjuntando…' : `Adjuntar como v${version + 1}`}
          </Button>
        </>
      )}>
      {/* R-1 del AT (§10.8): riesgo de un adjunto con datos identificativos. */}
      <p className="text-meta text-ink-soft mb-2">La carta no debe identificar a la persona afectada.</p>
      <Field label="Carta final" required hint={textoDeLimites} error={errores.archivo}>
        <SubidaDeArchivo aria-label="Carta final" archivo={archivo} onElegir={onArchivo} acepta={ACEPTA.pdf} maxMb={topeMb}
          data-testid="carta-form-archivo" />
      </Field>
      <Field label="Motivo" required error={errores.motivo}
        hint={sinMotivo ? MENSAJES.sinMotivo : undefined}
        contador={<Contador valor={motivo} tope={TOPE_DEL_MOTIVO} />}>
        <Textarea value={motivo} rows={3} maxLength={TOPE_DEL_MOTIVO} spellCheck lang="es"
          onChange={(e) => onMotivo(e.target.value)} onBlur={() => onSalirDe('motivo')} data-testid="carta-form-motivo-adjunto" />
      </Field>
    </Panel>
  );
}

/** CC-01-C5: editar o adjuntar con la firma pedida anula la solicitud; se confirma antes. */
export function ConfirmarAnularLaFirma({ efecto, ocupado, onConfirmar, onVolver }: Readonly<{
  efecto: string | null; ocupado: boolean; onConfirmar: () => void; onVolver: () => void;
}>) {
  return (
    <Modal open={efecto !== null} onClose={onVolver} tone="warning" eyebrow="Firma solicitada" title={efecto ?? ''}
      data-testid="carta-modal-anular-firma"
      footer={(
        <>
          <Button variant="secondary" onClick={onVolver} disabled={ocupado} data-testid="carta-volver-sin-anular">Volver</Button>
          <Button onClick={onConfirmar} disabled={ocupado} data-testid="carta-confirmar-anular-firma">Guardar y anular la solicitud</Button>
        </>
      )}>
      <p className="text-compacto text-ink-soft">La versión nueva queda preparada y la firma se pide sobre ella.</p>
    </Modal>
  );
}

/** «Confirmación antes de descartar cambios» del catálogo de patrones. */
export function ConfirmarDescartar({ abierto, onDescartar, onSeguir }: Readonly<{
  abierto: boolean; onDescartar: () => void; onSeguir: () => void;
}>) {
  return (
    <Modal open={abierto} onClose={onSeguir} tone="warning" title="¿Descartar la edición?" data-testid="carta-modal-descartar"
      footer={(
        <>
          <Button variant="secondary" onClick={onSeguir} data-testid="carta-seguir-editando">Seguir editando</Button>
          <Button variant="danger" onClick={onDescartar} data-testid="carta-descartar-edicion">Descartar</Button>
        </>
      )}>
      <p className="text-compacto text-ink-soft">El texto que escribiste se pierde.</p>
    </Modal>
  );
}
